'use client';

import React, { useState } from 'react';
import { useRouter } from 'next/navigation';

export default function BlogPage() {
  const router = useRouter();
  const [activeCategory, setActiveCategory] = useState('All');

  const blogPosts = [
    {
      id: 1,
      title: 'Top 10 Trekking Routes in Nepal for 2026',
      excerpt: 'From the iconic Everest Base Camp to the hidden gems of Dolpo, discover the best trekking routes Nepal has to offer this season.',
      category: 'Trekking',
      date: 'July 28, 2026',
      readTime: '8 min read',
      image: 'https://images.unsplash.com/photo-1544735716-392fe2489ffa?w=800&q=80',
      alt: 'Trekkers walking on a mountain trail in Nepal with snow-capped Himalayan peaks in the background',
      slug: 'top-10-trekking-routes-nepal-2026',
    },
    {
      id: 2,
      title: 'Pokhara Lakeside: A Complete Travel Guide',
      excerpt: 'Everything you need to know about visiting Pokhara — from Phewa Lake boat rides to paragliding over the Annapurna range.',
      category: 'Destinations',
      date: 'July 22, 2026',
      readTime: '6 min read',
      image: 'https://images.unsplash.com/photo-1605640840605-14ac1855827b?w=800&q=80',
      alt: 'Phewa Lake in Pokhara with colorful boats and Annapurna mountain range reflecting in the calm water',
      slug: 'pokhara-lakeside-complete-travel-guide',
    },
    {
      id: 3,
      title: 'Nepal Visa & Permits: Everything You Need to Know',
      excerpt: 'A step-by-step guide to obtaining your Nepal visa, TIMS card, Annapurna Conservation Area Permit, and Sagarmatha National Park entry.',
      category: 'Travel Tips',
      date: 'July 15, 2026',
      readTime: '5 min read',
      image: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=800&q=80',
      alt: 'Nepal travel documents and passport with visa stamps on a wooden desk',
      slug: 'nepal-visa-permits-guide',
    },
    {
      id: 4,
      title: 'Best Time to Visit Nepal: Month-by-Month Guide',
      excerpt: 'Whether you are planning a trek, a cultural tour, or a wildlife safari, timing your Nepal visit right makes all the difference.',
      category: 'Travel Tips',
      date: 'July 10, 2026',
      readTime: '7 min read',
      image: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=800&q=80',
      alt: 'Colorful rhododendron flowers blooming on a Himalayan hillside in Nepal during spring season',
      slug: 'best-time-to-visit-nepal',
    },
    {
      id: 5,
      title: 'Chitwan National Park: Wildlife Safari Guide',
      excerpt: 'Spot one-horned rhinos, Bengal tigers, and gharial crocodiles in one of Asia\'s finest wildlife reserves.',
      category: 'Wildlife',
      date: 'July 5, 2026',
      readTime: '6 min read',
      image: 'https://images.unsplash.com/photo-1564760055775-d63b17a55c44?w=800&q=80',
      alt: 'One-horned rhinoceros grazing in the grasslands of Chitwan National Park in Nepal',
      slug: 'chitwan-national-park-wildlife-safari-guide',
    },
    {
      id: 6,
      title: 'Kathmandu Valley: 7 UNESCO Heritage Sites in 3 Days',
      excerpt: 'A practical itinerary to visit Pashupatinath, Boudhanath, Swayambhunath, Bhaktapur Durbar Square, and more.',
      category: 'Culture',
      date: 'June 28, 2026',
      readTime: '9 min read',
      image: 'https://images.unsplash.com/photo-1582650625119-3a31f8fa2699?w=800&q=80',
      alt: 'Boudhanath Stupa in Kathmandu with prayer flags and pilgrims walking around the sacred Buddhist monument',
      slug: 'kathmandu-valley-unesco-heritage-sites',
    },
  ];

  const categories = ['All', 'Trekking', 'Destinations', 'Travel Tips', 'Wildlife', 'Culture', 'Food', 'Adventure'];

  const filteredPosts = activeCategory === 'All'
    ? blogPosts
    : blogPosts?.filter(p => p?.category === activeCategory);

  return (
    <div className="min-h-screen bg-navy pt-20">
      {/* Hero */}
      <div className="bg-gradient-to-b from-navy-mid to-navy px-4 sm:px-6 py-12 sm:py-16 text-center">
        <div className="max-w-3xl mx-auto">
          <span className="inline-block text-xs font-semibold tracking-widest text-gold uppercase mb-4 bg-gold/10 border border-gold/20 px-4 py-1.5 rounded-full">
            Travel Blog
          </span>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-serif font-bold text-snow mb-4">
            Stories from <span className="text-gold italic">Nepal</span>
          </h1>
          <p className="text-snow/60 text-base sm:text-lg leading-relaxed">
            Travel guides, trekking tips, cultural insights, and hidden gems — everything you need to plan your perfect Nepal adventure.
          </p>
        </div>
      </div>
      {/* Category filter */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <div className="flex gap-2 flex-wrap">
          {categories?.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                cat === activeCategory
                  ? 'bg-gold text-navy' :'bg-white/5 text-snow/70 hover:bg-white/10 hover:text-snow border border-white/10'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>
      </div>
      {/* Blog grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 pb-20">
        {filteredPosts?.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-snow/40 text-lg">No articles found in this category yet.</p>
            <button
              onClick={() => setActiveCategory('All')}
              className="mt-4 text-gold hover:underline text-sm"
            >
              View all articles →
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
            {filteredPosts?.map((post) => (
              <article
                key={post?.id}
                className="yn-card rounded-2xl overflow-hidden group cursor-pointer"
                onClick={() => router?.push(`/search?q=${encodeURIComponent(post?.title)}`)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e?.key === 'Enter') router?.push(`/search?q=${encodeURIComponent(post?.title)}`); }}
              >
                <div className="relative h-48 sm:h-52 overflow-hidden">
                  <img
                    src={post?.image}
                    alt={post?.alt}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-navy/60 to-transparent" />
                  <span className="absolute top-3 left-3 text-xs font-semibold bg-gold text-navy px-2.5 py-1 rounded-full">
                    {post?.category}
                  </span>
                </div>
                <div className="p-4 sm:p-5">
                  <div className="flex items-center gap-3 text-xs text-snow/40 mb-3">
                    <span>{post?.date}</span>
                    <span>·</span>
                    <span>{post?.readTime}</span>
                  </div>
                  <h2 className="text-snow font-semibold text-sm sm:text-base leading-snug mb-2 group-hover:text-gold transition-colors line-clamp-2">
                    {post?.title}
                  </h2>
                  <p className="text-snow/50 text-sm leading-relaxed line-clamp-3">{post?.excerpt}</p>
                  <div className="mt-4 flex items-center gap-1 text-gold text-sm font-medium">
                    Read more
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="group-hover:translate-x-1 transition-transform">
                      <path d="M5 12h14M12 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}

        {/* Load more */}
        <div className="text-center mt-10 sm:mt-12">
          <button
            className="btn-outline-gold px-8 py-3 rounded-full text-sm font-medium"
            onClick={() => router?.push('/search?category=blog')}
          >
            Load More Articles
          </button>
        </div>
      </div>
    </div>
  );
}
